/**
 * ExcludeGroupDateRange — the metric window a rule's conditions are measured
 * over. NOT optional: unset, the rule INHERITS the first applicable formula
 * group's window (else last-7-days), so a window is always in force — the
 * control OVERRIDES it, it does not add one. The only case with no window at
 * all is a group made purely of score/tag conditions, which are read live.
 * Set = the shared date-range dropdown + a clear (×). Reuses the formula-group
 * date picker so the two read identically.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FormulaGroup } from '@trafficpulse/shared';

import { NoWindowNote, RuleRangeChoice, type RangeValue } from './group/range/RuleRangeChoice';
import type { ExcludeViewGroup, ExcludeViewItem } from '../../utils/exclude-view-model';
import { defaultRuleDateRange } from '../../utils/default-rule-date-range';

export interface ExcludeGroupDateRangeProps {
  group: ExcludeViewGroup;
  onChange: (next: ExcludeViewGroup) => void;
  /** Live formula groups — an unset rule inherits the first applicable one's window. */
  formulaGroups: FormulaGroup[];
}

/** The leaf kind of a view item (unwraps a NOT). */
function leafKind(item: ExcludeViewItem): 'condition' | 'ranking' | 'tag' {
  return item.kind === 'leaf' ? item.node.kind : item.child.kind;
}

/**
 * Does this group measure anything over a window? Only NUMERIC metric
 * conditions do. Score-ranking reads the optimizer's live score and flow-tag
 * reads the live earned tag — neither uses a window.
 */
export function groupUsesDateWindow(group: ExcludeViewGroup): boolean {
  const kinds = group.children.map(leafKind);
  return !(
    !kinds.includes('condition') && kinds.some((k) => k === 'ranking' || k === 'tag')
  );
}

const ExcludeGroupDateRange: FC<ExcludeGroupDateRangeProps> = ({
  group,
  onChange,
  formulaGroups,
}) => {
  // The date range only feeds NUMERIC metric conditions (they read a metric over
  // that window). Score-ranking reads the optimizer's live score and flow-tag
  // reads the live earned tag — neither uses a window. So a group made only of
  // score/tag conditions has no use for a date range; say so instead of offering
  // an inert control.
  if (!groupUsesDateWindow(group)) {
    return (
      <NoWindowNote
        text="No date window needed — tags and score are read live"
        title="Score-ranking and flow-tag conditions read the optimizer's live score, not a metric window — no date range needed. Add a metric condition (e.g. Profit < 0) to pin one."
      />
    );
  }
  const def = defaultRuleDateRange(formulaGroups, group.appliesTo);
  const set = (range: RangeValue | undefined) =>
    onChange({
      ...group,
      dateRange: range?.dateRange,
      dateRangeCustomFrom: range?.dateRangeCustomFrom,
      dateRangeCustomTo: range?.dateRangeCustomTo,
    });
  const seed = def.seed;
  return (
    <RuleRangeChoice
      pinned={
        group.dateRange
          ? {
              dateRange: group.dateRange,
              dateRangeCustomFrom: group.dateRangeCustomFrom,
              dateRangeCustomTo: group.dateRangeCustomTo,
            }
          : null
      }
      inheritedLabel={def.unifiedLabel ?? 'varies by layer'}
      inheritedTitle={inheritedTitle(def)}
      onPin={() =>
        set({
          dateRange: seed.dateRange,
          dateRangeCustomFrom: seed.dateRange === 'custom' ? seed.from : undefined,
          dateRangeCustomTo: seed.dateRange === 'custom' ? seed.to : undefined,
        })
      }
      onCommit={set}
      onClear={() => set(undefined)}
    />
  );
};

/** Why the inherited window is what it is — the hover on the dashed chip. */
function inheritedTitle(def: ReturnType<typeof defaultRuleDateRange>): string {
  return def.unifiedLabel
    ? `Inherited from the first formula group: ${def.unifiedLabel}. This window is already in force — click only to pin a different one to this rule.`
    : `Inherited per layer: ${def.perLayer
        .map((p) => `${p.layer} → ${p.label}`)
        .join(' · ')}. Already in force — click only to pin a different one.`;
}

export default ExcludeGroupDateRange;
